import React, { useEffect, useState } from 'react';
import {
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  Modal,
  Row,
  Select,
  Spin,
  Typography,
  message,
} from 'antd';
import { debounce, startCase } from 'lodash';

import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';
import { TableList } from 'components/table-list';

import PromoSectionServices from 'services/promo-section';
import ProductServices from 'services/product';

import { errHandling } from 'utils';
import {
  percentageInputNumberFormatter,
  percentageInputNumberParser,
} from 'utils/inputNumber';

import './style.scss';
import { useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Option } = Select;
const { Text, Title } = Typography;
const { confirm } = Modal;

const FIELDS = {
  DISCOUNT: 'discount',
  IMAGE: 'image',
  PRODUCTS: 'collection_items_attributes',
  TITLE: 'title',
  TITLE_BAHASA: 'title_idn',
};

export function PromoSectionCreateEdit() {
  const [form] = Form.useForm();
  const [fetchingProduct, setFetchingProduct] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [productList, setProductList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(undefined);
  const [showModal, setShowModal] = useState(false);
  const [value, setValue] = useState({});
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(location.search);
  const sectionId = urlParams.get('id');

  const formItemLayout = {
    labelCol: { span: 5 },
    wrapperCol: { span: 16, offset: 3 },
    labelAlign: 'left',
  };

  const notFoundContent = fetchingProduct ? <Spin size="small" /> : null;

  const buttonAction = ({ id }) => (
    <Col lg={16} className="button">
      <PdButton
        className="height-32"
        type="danger"
        full
        onClick={handleDelete.bind(this, id)}>
        Delete
      </PdButton>
    </Col>
  );

  const columns = [
    {
      name: 'Title',
      selector: row => row.title,
      center: true,
    },
    {
      name: 'SKU',
      selector: row => row.sku,
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      right: true,
      width: '25%',
    },
  ];

  function handleDelete(productId) {
    confirm({
      title: 'Are you sure to delete this product?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        setValue((prev) => {
          const newProduct = [...prev[FIELDS.PRODUCTS]].filter(
            ({ id }) => productId !== id
          );
          return { ...prev, [FIELDS.PRODUCTS]: newProduct };
        });
      },
    });
  }

  async function onFinish(values) {
    setLoadingButton(true);
    const products = value[FIELDS.PRODUCTS].map(({ id }) => ({
      product_id: id,
    }));

    const payload = {
      ...values,
      show_in_home: true,
      [FIELDS.PRODUCTS]: products,
    };
    try {
      if (sectionId) {
        await PromoSectionServices.updatePromoSection(sectionId, payload);
      } else {
        const { data: { data: { id: createId } = {} } = {} } =
          await PromoSectionServices.createPromoSection(payload);
        await PromoSectionServices.deactivatePromoSection(createId);
      }
      message.success(
        sectionId ? 'Successfully edit section' : 'Successfully add new section'
      );
      navigate('/promo-section');
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }

  async function fetchPromoSectionDetail() {
    setLoadingPage(true);
    try {
      const {
        data: { data = {} },
      } = await PromoSectionServices.getPromoSectionDetail(sectionId);
      const { discount, image, products = [], title, title_idn } = data;

      setValue({
        [FIELDS.DISCOUNT]: discount,
        [FIELDS.IMAGE]: image,
        [FIELDS.PRODUCTS]: products,
        [FIELDS.TITLE]: title,
        [FIELDS.TITLE_BAHASA]: title_idn,
      });

      form.setFieldsValue({
        [FIELDS.DISCOUNT]: discount,
        [FIELDS.IMAGE]: image,
        [FIELDS.TITLE]: title,
        [FIELDS.TITLE_BAHASA]: title_idn,
      });
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingPage(false);
    }
  }

  function handleCloseModal() {
    setSelectedProduct(undefined);
    setShowModal(false);
  }

  function handleOnChangeProduct(selectedId) {
    setSelectedProduct(selectedId);
  }

  function handleUploadImg(imgUrl) {
    form.setFieldsValue({ [FIELDS.IMAGE]: imgUrl });
    setValue((prev) => ({ ...prev, [FIELDS.IMAGE]: imgUrl }));
  }

  async function handleSubmitProduct() {
    setLoadingButton(true);
    try {
      const {
        data: { data = {} },
      } = await ProductServices.getProductDetail(selectedProduct);
      setValue((prev) => ({
        ...prev,
        [FIELDS.PRODUCTS]: [...prev[FIELDS.PRODUCTS], data],
      }));
      handleCloseModal();
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingButton(false);
    }
  }

  async function fetchProduct(q) {
    setFetchingProduct(true);
    try {
      const {
        data: { data = [] },
      } = await ProductServices.getProductList({
        status: 'active',
        q,
      });
      setProductList(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setFetchingProduct(false);
    }
  }

  useEffect(() => {
    if (sectionId) {
      fetchPromoSectionDetail();
    } else {
      setValue((prev) => ({ ...prev, [FIELDS.PRODUCTS]: [] }));
    }

    // eslint-disable-next-line
  }, []);

  return (
    <Layout className="main-container pd-cms-promo-section">
      <Form
        className="pd-promo-section-form"
        form={form}
        onFinish={onFinish}>
        <div className="content">
          <div className="pageTitle">
            {sectionId ? 'Edit' : 'Add'} Promo Section
          </div>
          <Content className="pageContent">
            <Row gutter={[18, 36]}>
              <Col lg={16}>
                <Form.Item
                  label="Title"
                  name={FIELDS.TITLE}
                  {...formItemLayout}
                  rules={[
                    {
                      message: 'Please input section title!',
                      required: true,
                    },
                  ]}>
                  <Input placeholder="Input title here" />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4, offset: 4 }}>
                <PdButton
                  className="height-32"
                  type="primary"
                  full
                  onClick={setShowModal.bind(this, true)}>
                  Add Product
                </PdButton>
              </Col>
            </Row>
            <Row gutter={[18, 36]}>
              <Col lg={16}>
                <Form.Item
                  label="Title (bahasa)"
                  name={FIELDS.TITLE_BAHASA}
                  {...formItemLayout}
                  rules={[
                    {
                      message: 'Please input section title bahasa!',
                      required: true,
                    },
                  ]}>
                  <Input placeholder="Input title bahasa here" />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col lg={16}>
                <Form.Item
                  label="Discount"
                  name={FIELDS.DISCOUNT}
                  {...formItemLayout}>
                  <InputNumber
                    formatter={percentageInputNumberFormatter}
                    parser={percentageInputNumberParser}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col lg={16}>
                <Form.Item
                  label="Image(376x150)"
                  name={FIELDS.IMAGE}
                  {...formItemLayout}>
                  <PdUploader
                    alt="uploaded-img"
                    afterUpload={handleUploadImg}
                    imgUrl={value[FIELDS.IMAGE]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Form.Item label="Products">
                {!loadingPage && (
                  <TableList
                    columns={columns}
                    loadingOrder={loadingPage}
                    orderList={value[FIELDS.PRODUCTS]}
                    page={1}
                    responsive={false}
                  />
                )}
              </Form.Item>
            </Row>
            <Row type="flex" justify="center">
              <Col lg={12}>
                <Form.Item>
                  <PdButton
                    className="w-full height-32"
                    full
                    htmlType="submit"
                    loading={loadingButton}
                    type="primary">
                    Submit
                  </PdButton>
                </Form.Item>
              </Col>
            </Row>
          </Content>
        </div>
      </Form>

      {/* Modal Section */}
      {showModal && (
        <Modal
          open={showModal}
          wrapClassName="hidden-wrap-modal"
          centered
          onCancel={handleCloseModal}
          footer={false}>
          <Row type="flex" justify="center">
            <Title level={4}>Add Product</Title>
          </Row>
          <Row className="pd-margin-top-md">
            <Text>Select Product: </Text>
          </Row>
          <Row>
            <Select
              className="w-full pd-margin-top-sm"
              filterOption={false}
              notFoundContent={notFoundContent}
              onChange={handleOnChangeProduct}
              onSearch={debounce(fetchProduct, 500)}
              placeholder="Select active product"
              showSearch>
              {productList.map(({ id = '', title = '' }) => (
                <Option key={id} value={id}>
                  {startCase(title)}
                </Option>
              ))}
            </Select>
          </Row>
          <Row type="flex" justify="center" className="pd-margin-top-lg">
            <Col lg={12}>
              <PdButton
                className="w-full height-32"
                full
                loading={loadingButton}
                onClick={handleSubmitProduct}
                type="primary">
                Save
              </PdButton>
            </Col>
          </Row>
        </Modal>
      )}
    </Layout>
  );
}

export default PromoSectionCreateEdit;
